/**
 * Cast cursor — armed-cursor state machine for point/unit-targeted abilities.
 *
 * State:
 *   "idle"  — no ability armed
 *   "armed" — cursor shows that an ability is queued for next click
 *
 * Transitions:
 *   - press Q/W/E/R: arm matching slot's ability (if targeting requires cursor)
 *                    OR cast immediately for self-targeted
 *   - press same key again: idle (toggle off)
 *   - press different ability key while armed: re-arm to that ability (no cost spent)
 *   - press Esc: cancel
 *   - right-click: cancel
 *   - left-click while armed: send `castIntent`
 *
 * Spec: docs/design/09-abilities.md §"Targeting modes" + §"Cancellation"
 */
import type { Cube, AbilityId } from "@tribez/shared";
import { ABILITIES } from "@tribez/shared";
import type { Room } from "colyseus.js";

export interface CastCursor {
  pressSlot(slot: "Q" | "W" | "E" | "R"): void;
  cancel(): void;
  /** Called when the player left-clicks the world. */
  worldClick(point: { x: number; z: number }, hitUnitId?: string): void;
  armed(): AbilityId | null;
}

export interface CastCursorDeps {
  getCube: () => Cube | null;
  getRoom: () => Room | null;
  /** Optional visual feedback hook (called whenever armed state changes). */
  onArmedChange?: (armed: AbilityId | null) => void;
  /** Fired when a Q/W/E/R slot is pressed but holds no ability (teach-the-Tavern cue). */
  onEmptySlot?: (slot: "Q" | "W" | "E" | "R") => void;
  /** Fired when an armed unit-target cast is released on no valid unit (reject cue). */
  onNoTarget?: (armed: AbilityId) => void;
  /**
   * Fired the instant a castIntent is dispatched (before the server confirms) so
   * the caller can play optimistic cosmetic feedback (windup anim). Slice 2.
   */
  onCast?: (abilityId: AbilityId) => void;
}

export function createCastCursor(deps: CastCursorDeps): CastCursor {
  let armedAbilityId: AbilityId | null = null;

  function setArmed(next: AbilityId | null): void {
    if (armedAbilityId === next) return;
    armedAbilityId = next;
    deps.onArmedChange?.(next);
  }

  function pressSlot(slot: "Q" | "W" | "E" | "R"): void {
    const cube = deps.getCube();
    if (!cube) return;

    const slotKey = `slot${slot}` as "slotQ" | "slotW" | "slotE" | "slotR";
    const id = (cube.spellbook as unknown as Record<string, string>)[slotKey];
    if (!id) { deps.onEmptySlot?.(slot); return; }

    const def = ABILITIES[id];
    if (!def) return;

    const room = deps.getRoom();

    // Self-targeted casts fire immediately
    if (def.targeting.mode === "self") {
      room?.send("castIntent", { abilityId: id });
      deps.onCast?.(id);
      setArmed(null); // clear any prior armed state
      return;
    }

    // Toggle / channel: out of scope for v1 fixtures; just send and let the server reject
    if (def.targeting.mode === "toggle" || def.targeting.mode === "channel") {
      room?.send("castIntent", { abilityId: id });
      deps.onCast?.(id);
      setArmed(null);
      return;
    }

    // Re-press same slot while armed → cancel (toggle off)
    if (armedAbilityId === id) {
      setArmed(null);
      return;
    }

    // Arm (or re-arm with a different ability)
    setArmed(id);
  }

  function cancel(): void {
    setArmed(null);
  }

  function worldClick(point: { x: number; z: number }, hitUnitId?: string): void {
    if (!armedAbilityId) return;
    const def = ABILITIES[armedAbilityId];
    if (!def) {
      setArmed(null);
      return;
    }

    const intent: { abilityId: string; targetUnitId?: string; targetX?: number; targetZ?: number } = {
      abilityId: armedAbilityId,
    };

    if (def.targeting.mode === "unit") {
      if (!hitUnitId) {
        // No unit hit; cancel the cast (don't dispatch to server) — surface a
        // reject cue so it doesn't read as a silent fizzle.
        deps.onNoTarget?.(armedAbilityId);
        setArmed(null);
        return;
      }
      intent.targetUnitId = hitUnitId;
    } else {
      // point or point-charge
      intent.targetX = point.x;
      intent.targetZ = point.z;
    }

    deps.getRoom()?.send("castIntent", intent);
    deps.onCast?.(armedAbilityId);
    setArmed(null);
  }

  return {
    pressSlot,
    cancel,
    worldClick,
    armed: () => armedAbilityId,
  };
}
